import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils } from '../orpc'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'

// ============ QUERIES ============

export function useTasksByTripLeg(tripLegId: string | null) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery({
    ...orpc.task.getByTripLeg.queryOptions({ input: { tripLegId: tripLegId! } }),
    enabled: !!tripLegId,
  })
}

export function useTask(id: string | null) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery({
    ...orpc.task.getById.queryOptions({ input: { id: id! } }),
    enabled: !!id,
  })
}

export function useTasksToReview(enabled = true) {
  const orpc = useOrpcTanstackQueryUtils()
  const { isAdmin } = useIsAdmin()
  return useQuery({
    ...orpc.task.getSubmittedForReview.queryOptions(),
    enabled: enabled && isAdmin,
  })
}

export function useArchivedTasks() {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.task.getReadArchived.queryOptions())
}

export function useTasksByAircraft(aircraftId: string | null) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery({
    ...orpc.task.getByAircraft.queryOptions({
      input: { aircraftId: aircraftId! },
    }),
    enabled: !!aircraftId,
  })
}

export function useMyDraftTasks() {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.task.getMyDrafts.queryOptions())
}

export function useMyTasks() {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.task.getMyTasks.queryOptions())
}

// ============ MUTATIONS ============

export function useTaskAttach() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.task.attach.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.task.key() })
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

export function useTaskStart() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.task.start.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.task.key() })
      },
    })
  )
}

export function useTaskDetach() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.task.detach.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.task.key() })
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

export function useTaskItemsUpdate() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.task.updateItems.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.task.key() })
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

export function useTaskSubmit() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.task.submit.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.task.key() })
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

export function useTaskRevertToDraft() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.task.revertToDraft.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.task.key() })
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

export function useTaskAddPhoto() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.task.addPhoto.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.task.key() })
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

export function useTaskRemovePhoto() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.task.removePhoto.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.task.key() })
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

export function useTaskAddItemPhoto() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.task.addItemPhoto.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.task.key() })
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

export function useTaskRemoveItemPhoto() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.task.removeItemPhoto.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.task.key() })
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

export function useTaskMarkRead() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.task.markRead.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.task.key() })
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}
